import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Target, 
  FileText, 
  BookOpen, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  Home,
  ExternalLink,
  Lightbulb,
  TrendingUp,
  Link as LinkIcon
} from "lucide-react";
import { ModernMCQCard, type MCQ } from "@/components/practice/ModernMCQCard";
import ReactMarkdown from "react-markdown";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { FloatingNavigationBar } from "@/components/navigation/FloatingNavigationBar";
import { useQuestionStatus } from "@/hooks/useQuestionStatus";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { useNavigationKeyboard } from "@/hooks/useNavigationKeyboard";

interface UnifiedContentViewerProps {
  topicId: string;
  initialMode?: 'mcq' | 'notes' | 'mixed';
  onTopicChange?: (topicId: string) => void;
  className?: string;
}

interface Note {
  id: string;
  title: string;
  content: string;
}

interface ProcessedMCQ extends MCQ {
  originalId: string;
}

export const UnifiedContentViewer = ({ 
  topicId, 
  initialMode = 'mixed', 
  onTopicChange,
  className = '' 
}: UnifiedContentViewerProps) => {
  const [activeTab, setActiveTab] = useState<'mcq' | 'notes' | 'mixed'>(initialMode);
  const [mcqIndex, setMcqIndex] = useState(0);
  const [noteIndex, setNoteIndex] = useState(0);
  const [mixedIndex, setMixedIndex] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch topic info
  const { data: topic } = useQuery({
    queryKey: ["topic", topicId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topics")
        .select("id, name")
        .eq("id", topicId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Fetch MCQs for this topic (hierarchical: parent + all children)
  const { data: mcqData = [] } = useQuery({
    queryKey: ["topic-mcqs", topicId],
    queryFn: async () => {
      // Fetch child topics
      const { data: childTopics, error: childError } = await supabase
        .from("topics")
        .select("id")
        .eq("parent_topic_id", topicId);
      
      if (childError) throw childError;
      
      const topicIds = [topicId, ...(childTopics || []).map(t => t.id)];
      
      const { data, error } = await supabase
        .from("mcqs")
        .select("id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation, difficulty, tags")
        .eq("lifecycle_status", "active")
        .in("topic_id", topicIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      
      return (data || []).map((q: any): ProcessedMCQ => ({
        originalId: q.id,
        id: q.id,
        stem: q.stem,
        options: [
          q.option_a,
          q.option_b,
          q.option_c,
          q.option_d,
          q.option_e
        ].filter(Boolean),
        correctIndex: ['A', 'B', 'C', 'D', 'E'].indexOf(q.correct_option),
        explanation: q.explanation || undefined,
        tags: q.tags || [],
        difficulty: q.difficulty,
      }));
    },
  });

  // Fetch Notes for this topic (hierarchical: parent + all children)
  const { data: notes = [] } = useQuery({
    queryKey: ["topic-notes", topicId],
    queryFn: async () => {
      // Fetch child topics
      const { data: childTopics, error: childError } = await supabase
        .from("topics")
        .select("id")
        .eq("parent_topic_id", topicId);
      
      if (childError) throw childError;
      
      const topicIds = [topicId, ...(childTopics || []).map(t => t.id)];
      
      const { data, error } = await supabase
        .from("notes")
        .select("id, title, content")
        .in("topic_id", topicIds)
        .order("created_at", { ascending: false});
      if (error) throw error;
      return (data || []) as Note[];
    },
  });

  // Fetch related MCQ-Note links
  const { data: contentLinks = [] } = useQuery({
    queryKey: ["content-links", topicId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mcq_note_links")
        .select(`
          mcq_id,
          note_id,
          relevance_score
        `);
      if (error) throw error;
      return data || [];
    },
  });

  // Create mixed content (interleaved MCQs and Notes)
  const mixedContent = useMemo(() => {
    const mixed: Array<{ type: 'mcq' | 'note'; index: number; item: ProcessedMCQ | Note }> = [];
    
    // Add MCQs
    mcqData.forEach((mcq, index) => {
      mixed.push({ type: 'mcq', index, item: mcq });
    });
    
    // Add Notes
    notes.forEach((note, index) => {
      mixed.push({ type: 'note', index, item: note });
    });
    
    // Sort by some intelligent logic (e.g., difficulty, reading time, etc.)
    return mixed.sort((a, b) => {
      if (a.type === 'note' && b.type === 'mcq') return -1; // Notes first for learning
      if (a.type === 'mcq' && b.type === 'note') return 1;
      return 0;
    });
  }, [mcqData, notes]);

  // Question status management for MCQs
  const {
    questionStatuses,
    toggleFlag,
    markAnswered,
    getQuestionStatus,
    resetQuestionStatus
  } = useQuestionStatus(mcqData.length);

  // Handle MCQ submission from MCQ tab (uses mcqIndex)
  const onSubmitMCQTab = (selected: number, correct: boolean) => {
    const currentMCQ = mcqData[mcqIndex];
    if (!currentMCQ || !userId) return;
    markAnswered(mcqIndex, selected, correct);
    supabase
      .from("attempts")
      .insert({ user_id: userId, mcq_id: currentMCQ.originalId, is_correct: correct })
      .then(({ error }) => {
        if (error) {
          toast({ title: "Could not save attempt", description: error.message, variant: "destructive" });
        }
      });
  };

  // Handle MCQ submission from Mixed tab (uses the actual mixed item's MCQ id)
  const onSubmitMixedMCQ = (mcq: ProcessedMCQ) => (selected: number, correct: boolean) => {
    if (!userId) return;
    // Mark in status by mcqData index of this MCQ so MCQ tab stays consistent
    const idx = mcqData.findIndex(m => m.originalId === mcq.originalId);
    if (idx >= 0) markAnswered(idx, selected, correct);
    supabase
      .from("attempts")
      .insert({ user_id: userId, mcq_id: mcq.originalId, is_correct: correct })
      .then(({ error }) => {
        if (error) {
          toast({ title: "Could not save attempt", description: error.message, variant: "destructive" });
        }
      });
  };

  // Navigation functions
  const goToPreviousMCQ = () => setMcqIndex(i => Math.max(0, i - 1));
  const goToNextMCQ = () => setMcqIndex(i => Math.min(i + 1, mcqData.length - 1));
  const goToPreviousNote = () => setNoteIndex(i => Math.max(0, i - 1));
  const goToNextNote = () => setNoteIndex(i => Math.min(i + 1, notes.length - 1));
  const goToPreviousMixed = () => setMixedIndex(i => Math.max(0, i - 1));
  const goToNextMixed = () => setMixedIndex(i => Math.min(i + 1, mixedContent.length - 1));

  // Keyboard navigation
  useNavigationKeyboard({
    onNext: activeTab === 'mcq' ? goToNextMCQ : activeTab === 'notes' ? goToNextNote : goToNextMixed,
    onPrevious: activeTab === 'mcq' ? goToPreviousMCQ : activeTab === 'notes' ? goToPreviousNote : goToPreviousMixed,
    onFlag: activeTab === 'mcq' ? () => toggleFlag(mcqIndex) : undefined,
    enabled: true
  });

  // Swipe navigation
  useSwipeNavigation({
    onSwipeLeft: activeTab === 'mcq' ? goToNextMCQ : activeTab === 'notes' ? goToNextNote : goToNextMixed,
    onSwipeRight: activeTab === 'mcq' ? goToPreviousMCQ : activeTab === 'notes' ? goToPreviousNote : goToPreviousMixed,
    enabled: true
  });

  // Get related content
  const getRelatedContent = (currentType: 'mcq' | 'note', currentId: string) => {
    return contentLinks
      .filter(link => 
        (currentType === 'mcq' && link.mcq_id === currentId) ||
        (currentType === 'note' && link.note_id === currentId)
      )
      .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
      .slice(0, 3);
  };

  const currentMCQ = mcqData[mcqIndex];
  const currentNote = notes[noteIndex];
  const currentMixed = mixedContent[mixedIndex];

  return (
    <div className={`space-y-6 animate-fade-in ${className}`}>
      {/* Header with Topic Info */}
      <div className="medical-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onTopicChange?.('hub')}
              className="text-muted-foreground hover:text-foreground"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Hub
            </Button>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold gradient-text">{topic?.name}</h1>
          </div>
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Target className="h-5 w-5 text-primary" />
            <div>
              <div className="font-semibold">{mcqData.length}</div>
              <div className="text-sm text-muted-foreground">MCQs</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-accent" />
            <div>
              <div className="font-semibold">{notes.length}</div>
              <div className="text-sm text-muted-foreground">Notes</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="font-semibold">{notes.reduce((sum, n) => sum + Math.ceil((n.content?.length || 500) / 200), 0)} min</div>
              <div className="text-sm text-muted-foreground">Reading time</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LinkIcon className="h-5 w-5 text-purple-500" />
            <div>
              <div className="font-semibold">{contentLinks.length}</div>
              <div className="text-sm text-muted-foreground">Links</div>
            </div>
          </div>
        </div>

        {/* Content Type Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="mcq" disabled={mcqData.length === 0}>
              <Target className="h-4 w-4 mr-2" />
              MCQs ({mcqData.length})
            </TabsTrigger>
            <TabsTrigger value="notes" disabled={notes.length === 0}>
              <FileText className="h-4 w-4 mr-2" />
              Notes ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="mixed" disabled={mixedContent.length === 0}>
              <BookOpen className="h-4 w-4 mr-2" />
              Mixed ({mixedContent.length})
            </TabsTrigger>
          </TabsList>

          {/* MCQ Content */}
          <TabsContent value="mcq" className="space-y-6 mt-6">
            {currentMCQ && (
              <>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">
                    MCQ {mcqIndex + 1} of {mcqData.length}
                  </Badge>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousMCQ}
                      disabled={mcqIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextMCQ}
                      disabled={mcqIndex >= mcqData.length - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <ModernMCQCard 
                  key={currentMCQ.originalId}
                  mcq={currentMCQ} 
                  onSubmit={onSubmitMCQTab}
                  onNext={goToNextMCQ}
                  onPrevious={goToPreviousMCQ}
                  hasNext={mcqIndex < mcqData.length - 1}
                  hasPrevious={mcqIndex > 0}
                  mode="practice"
                />

                {/* Related Notes */}
                {getRelatedContent('mcq', currentMCQ.originalId).length > 0 && (
                  <Card className="medical-card">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-500" />
                        Related Study Material
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {getRelatedContent('mcq', currentMCQ.originalId).map((link) => {
                          const relatedNote = notes.find(n => n.id === link.note_id);
                          if (!relatedNote) return null;
                          return (
                            <Button
                              key={link.note_id}
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => {
                                setActiveTab('notes');
                                setNoteIndex(notes.findIndex(n => n.id === link.note_id));
                              }}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {relatedNote.title}
                              <Badge variant="secondary" className="ml-auto text-xs">
                                {Math.round((link.relevance_score || 0) * 100)}% match
                              </Badge>
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Notes Content */}
          <TabsContent value="notes" className="space-y-6 mt-6">
            {currentNote && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">
                      Note {noteIndex + 1} of {notes.length}
                    </Badge>
                    <Badge variant="secondary">
                      <Clock className="h-3 w-3 mr-1" />
                      {Math.ceil((currentNote.content?.length || 500) / 200)} min read
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousNote}
                      disabled={noteIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextNote}
                      disabled={noteIndex >= notes.length - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <Card className="medical-card">
                  <CardHeader>
                    <CardTitle className="text-2xl gradient-text">
                      {currentNote.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <article className="prose prose-neutral max-w-none dark:prose-invert prose-lg">
                      <ReactMarkdown>{currentNote.content}</ReactMarkdown>
                    </article>
                  </CardContent>
                </Card>

                {/* Related MCQs */}
                {getRelatedContent('note', currentNote.id).length > 0 && (
                  <Card className="medical-card">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-green-500" />
                        Practice Questions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {getRelatedContent('note', currentNote.id).map((link) => {
                          const relatedMCQ = mcqData.find(m => m.originalId === link.mcq_id);
                          if (!relatedMCQ) return null;
                          return (
                            <Button
                              key={link.mcq_id}
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => {
                                setActiveTab('mcq');
                                setMcqIndex(mcqData.findIndex(m => m.originalId === link.mcq_id));
                              }}
                            >
                              <Target className="h-4 w-4 mr-2" />
                              {relatedMCQ.stem.slice(0, 60)}...
                              <Badge variant="secondary" className="ml-auto text-xs">
                                Level {relatedMCQ.difficulty}
                              </Badge>
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Mixed Content */}
          <TabsContent value="mixed" className="space-y-6 mt-6">
            {currentMixed && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">
                      {mixedIndex + 1} of {mixedContent.length}
                    </Badge>
                    <Badge variant={currentMixed.type === 'mcq' ? 'default' : 'secondary'}>
                      {currentMixed.type === 'mcq' ? (
                        <>
                          <Target className="h-3 w-3 mr-1" />
                          Practice Question
                        </>
                      ) : (
                        <>
                          <FileText className="h-3 w-3 mr-1" />
                          Study Material
                        </>
                      )}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousMixed}
                      disabled={mixedIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextMixed}
                      disabled={mixedIndex >= mixedContent.length - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {currentMixed.type === 'mcq' ? (
                  <ModernMCQCard 
                    key={(currentMixed.item as ProcessedMCQ).originalId}
                    mcq={currentMixed.item as ProcessedMCQ} 
                    onSubmit={onSubmitMixedMCQ(currentMixed.item as ProcessedMCQ)}
                    onNext={goToNextMixed}
                    onPrevious={goToPreviousMixed}
                    hasNext={mixedIndex < mixedContent.length - 1}
                    hasPrevious={mixedIndex > 0}
                    mode="practice"
                  />
                ) : (
                  <Card className="medical-card">
                    <CardHeader>
                      <CardTitle className="text-2xl gradient-text">
                        {(currentMixed.item as Note).title}
                      </CardTitle>
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        {Math.ceil(((currentMixed.item as Note).content?.length || 500) / 200)} min read
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      <article className="prose prose-neutral max-w-none dark:prose-invert prose-lg">
                        <ReactMarkdown>{(currentMixed.item as Note).content}</ReactMarkdown>
                      </article>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Show message if no content */}
      {mcqData.length === 0 && notes.length === 0 && (
        <Card className="medical-card p-12 text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No content available</h3>
          <p className="text-muted-foreground mb-4">
            This topic doesn't have any MCQs or notes yet. Check back later for updates.
          </p>
          <Button onClick={() => onTopicChange?.('hub')}>
            Browse Other Topics
          </Button>
        </Card>
      )}
    </div>
  );
};